import { createContext, useCallback, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Session } from '@clear/contracts';
import type { Role } from '@clear/domain';
import { api, ApiError, onAccessEnded, onUnauthorized } from './lib/api';
import { IS_DEMO_PATH } from './lib/base';
import { rememberUser } from './lib/storage';
import { outbox } from './offline/outbox';
import { useI18n } from './i18n';

interface SessionCtx {
  session: Session | null;
  loading: boolean;
  /** Called with the session returned by login / PIN setup. */
  signedIn: (s: Session) => void;
  logout: () => Promise<void>;
  switchRole: (r: Role) => Promise<void>;
}

const Ctx = createContext<SessionCtx | null>(null);

/** Set by an intentional logout so the login screen does not offer "continue where you were". */
let loggedOutOnPurpose = false;
export const consumeLoggedOut = () => {
  const v = loggedOutOnPurpose;
  loggedOutOnPurpose = false;
  return v;
};
export const SESSION_KEY = ['session'] as const;

export function SessionProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const { setLang } = useI18n();
  const q = useQuery({
    queryKey: SESSION_KEY,
    queryFn: async () => {
      try {
        return await api<Session>('/auth/session');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: (n, e) => !(e instanceof ApiError) && n < 2,
    // When offline at start-up, keep working from the last known session.
    networkMode: 'offlineFirst',
  });
  const session = q.data ?? null;

  useEffect(() => {
    outbox.setUser(session?.user.id ?? null);
  }, [session?.user.id]);

  useEffect(
    () =>
      onUnauthorized(() => {
        qc.setQueryData(SESSION_KEY, null);
      }),
    [qc],
  );
  useEffect(
    () =>
      onAccessEnded(() => {
        qc.setQueryData<Session | null>(SESSION_KEY, (s) => (s ? { ...s, accessEnded: true } : s));
      }),
    [qc],
  );

  const signedIn = useCallback(
    (s: Session) => {
      qc.setQueryData(SESSION_KEY, s);
      setLang(s.user.language);
      rememberUser(
        // The demo is shared by whoever is presenting: never remember the last person there.
        s.device.kind === 'personal' && !IS_DEMO_PATH
          ? {
              id: s.user.id,
              name: s.user.name,
              title: s.user.title,
              photoUrl: s.user.photoUrl,
              roles: s.user.roles,
            }
          : null,
      );
      void qc.invalidateQueries({ predicate: (x) => x.queryKey[0] !== 'session' });
    },
    [qc, setLang],
  );

  const logout = useCallback(async () => {
    loggedOutOnPurpose = true;
    if (IS_DEMO_PATH) rememberUser(null);
    try {
      await api('/auth/logout', { body: {} });
    } catch {
      /* offline logout still clears the screen */
    }
    qc.setQueryData(SESSION_KEY, null);
    qc.removeQueries({ predicate: (x) => x.queryKey[0] !== 'session' });
  }, [qc]);

  const switchRole = useCallback(
    async (role: Role) => {
      qc.setQueryData<Session | null>(SESSION_KEY, (s) => (s ? { ...s, role } : s));
      try {
        await api('/auth/role', { body: { role } });
      } catch {
        /* role is also enforced server-side per op; the toggle is a view preference */
      }
    },
    [qc],
  );

  // Shared department devices: log out after 15 minutes without a tap (PRD).
  const idleTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => {
    const ms = session?.idleTimeoutMs;
    if (!ms) return;
    const reset = () => {
      clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => void logout(), ms);
    };
    reset();
    const evs = ['pointerdown', 'keydown'] as const;
    evs.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    return () => {
      clearTimeout(idleTimer.current);
      evs.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [session?.idleTimeoutMs, logout]);

  const value = useMemo(
    () => ({ session, loading: q.isPending, signedIn, logout, switchRole }),
    [session, q.isPending, signedIn, logout, switchRole],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('SessionProvider missing');
  return v;
}

/** The session inside authenticated routes (guards guarantee it exists). */
export function useAuthed(): Session {
  const { session } = useSession();
  if (!session) throw new Error('not signed in');
  return session;
}
